import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import Sidebar from '../components/Sidebar';
import '../styles/theme.css';
import './Documents.css';

const DOC_TYPES = {
  cv:                 { label: 'CV / Resume',          icon: 'fa-file-alt',      required: true },
  transcript:         { label: 'Academic Transcript',  icon: 'fa-graduation-cap', required: false },
  matric_certificate: { label: 'Matric Certificate',   icon: 'fa-certificate',   required: false },
  degree_certificate: { label: 'Degree Certificate',   icon: 'fa-award',         required: false },
  diploma_certificate:{ label: 'Diploma Certificate',  icon: 'fa-scroll',        required: false },
  portfolio:          { label: 'Portfolio',            icon: 'fa-briefcase',     required: false },
  cover_letter:       { label: 'Cover Letter',         icon: 'fa-envelope',      required: false },
  reference_letter:   { label: 'Reference Letter',     icon: 'fa-user-check',    required: false },
  other:              { label: 'Other Document',       icon: 'fa-file',          required: false },
};

function Documents() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showModal, setShowModal] = useState(false);
  const [uploadData, setUploadData] = useState({ file: null, document_type: 'cv', description: '', is_primary: true });

  useEffect(() => { fetchDocuments(); fetchRequirements(); }, []);

  const fetchDocuments = async () => {
    try { const r = await api.get('/documents/'); setDocuments(r.data.documents); }
    catch { setDocuments([]); } finally { setLoading(false); }
  };

  const fetchRequirements = async () => {
    try { const r = await api.get('/documents/requirements'); setRequirements(r.data); } catch {}
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) return showMsg('Please select a file', 'error');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadData.file);
    fd.append('document_type', uploadData.document_type);
    fd.append('description', uploadData.description);
    fd.append('is_primary', uploadData.is_primary);
    try {
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg('Document uploaded successfully.');
      setShowModal(false);
      setUploadData({ file: null, document_type: 'cv', description: '', is_primary: true });
      fetchDocuments(); fetchRequirements();
    } catch (err) { showMsg('Upload failed: ' + (err.response?.data?.detail || err.message), 'error'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete(`/documents/${id}`); showMsg('Document deleted.'); fetchDocuments(); fetchRequirements(); }
    catch (err) { showMsg('Delete failed: ' + (err.response?.data?.detail || err.message), 'error'); }
  };

  const handleSetPrimary = async (id) => {
    try { await api.patch(`/documents/${id}/set-primary`); showMsg('Set as primary.'); fetchDocuments(); }
    catch (err) { showMsg('Error: ' + (err.response?.data?.detail || err.message), 'error'); }
  };

  const fmt = (bytes) => bytes < 1024 ? bytes + ' B' : bytes < 1048576 ? (bytes/1024).toFixed(1)+' KB' : (bytes/1048576).toFixed(1)+' MB';
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });

  const grouped = documents.reduce((acc, doc) => {
    (acc[doc.document_type] = acc[doc.document_type] || []).push(doc);
    return acc;
  }, {});

  const reqItems = requirements ? [
    { key: 'cv',             label: 'CV',             done: requirements.cv,             required: true },
    { key: 'transcript',     label: 'Transcript',     done: requirements.transcript,     required: false },
    { key: 'qualifications', label: 'Qualifications', done: requirements.qualifications, required: false },
  ] : [];

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">My Documents</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1>My Documents</h1>
              <p>Upload and manage your application documents</p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-upload"></i> Upload Document
            </button>
          </div>

          {message.text && (
            <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
              <i className={`fas ${message.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i>
              {message.text}
            </div>
          )}

          {/* Requirements card */}
          {requirements && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <span className="card-title"><i className="fas fa-tasks" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Document Checklist</span>
                {requirements.all_required_uploaded
                  ? <span className="badge badge-green"><i className="fas fa-check"></i> Ready to Apply</span>
                  : <span className="badge badge-orange"><i className="fas fa-exclamation"></i> Action Needed</span>}
              </div>
              <div className="docs-checklist">
                {reqItems.map(item => (
                  <div key={item.key} className={`docs-check-item ${item.done ? 'done' : item.required ? 'missing' : 'optional'}`}>
                    <i className={`fas ${item.done ? 'fa-check-circle' : item.required ? 'fa-times-circle' : 'fa-exclamation-circle'}`}></i>
                    <span>{item.label}</span>
                    <span className="docs-check-tag">{item.required ? 'Required' : 'Recommended'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document sections */}
          {loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Loading documents...</p>
            </div>
          ) : (
            <div className="docs-sections">
              {Object.entries(DOC_TYPES).map(([type, info]) => {
                const docs = grouped[type] || [];
                return (
                  <div key={type} className="card docs-section">
                    <div className="docs-section-header">
                      <div className="docs-section-title">
                        <i className={`fas ${info.icon}`} style={{ color: 'var(--primary)' }}></i>
                        <span>{info.label}</span>
                        {info.required && <span className="badge badge-red" style={{ fontSize: '0.7rem' }}>Required</span>}
                      </div>
                      <span className="badge badge-gray">{docs.length} file{docs.length !== 1 ? 's' : ''}</span>
                    </div>

                    {docs.length === 0 ? (
                      <div className="docs-empty-row">
                        <i className={`fas ${info.icon}`}></i>
                        <span>No {info.label.toLowerCase()} uploaded</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setUploadData(p => ({ ...p, document_type: type })); setShowModal(true); }}>
                          <i className="fas fa-plus"></i> Upload
                        </button>
                      </div>
                    ) : (
                      <div className="docs-file-list">
                        {docs.map(doc => (
                          <div key={doc.id} className="docs-file-row">
                            <div className="docs-file-icon"><i className={`fas ${info.icon}`}></i></div>
                            <div className="docs-file-info">
                              <div className="docs-file-name">{doc.file_name}</div>
                              <div className="docs-file-meta">
                                {fmtDate(doc.uploaded_at)} · {fmt(doc.file_size)}
                                {doc.is_primary && <span className="badge badge-blue" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>Primary</span>}
                              </div>
                            </div>
                            <div className="docs-file-actions">
                              {!doc.is_primary && (
                                <button className="btn btn-ghost btn-sm" onClick={() => handleSetPrimary(doc.id)} title="Set as primary">
                                  <i className="fas fa-star"></i>
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(doc.id)} title="Delete" style={{ color: 'var(--danger)' }}>
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-upload" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Upload Document</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleUpload}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Document Type</label>
                  <select className="form-control" value={uploadData.document_type}
                    onChange={e => setUploadData(p => ({ ...p, document_type: e.target.value }))}>
                    {Object.entries(DOC_TYPES).map(([v, i]) => (
                      <option key={v} value={v}>{i.label}{i.required ? ' (Required)' : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">File <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(PDF, DOC, DOCX, JPG, PNG — max 10MB)</span></label>
                  <input type="file" className="form-control" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" required
                    onChange={e => { const f = e.target.files[0]; if (f && f.size > 10485760) { showMsg('File too large (max 10MB)', 'error'); return; } setUploadData(p => ({ ...p, file: f })); }} />
                  {uploadData.file && (
                    <div className="form-hint"><i className="fas fa-file"></i> {uploadData.file.name} ({fmt(uploadData.file.size)})</div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Description <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <textarea className="form-control" rows="2" placeholder="Add a note..."
                    value={uploadData.description} onChange={e => setUploadData(p => ({ ...p, description: e.target.value }))} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={uploadData.is_primary}
                    onChange={e => setUploadData(p => ({ ...p, is_primary: e.target.checked }))} />
                  Set as primary document for this type
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
